import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface ErrorConfig {
	title: string;
	message: string;
	code: string;
}

const ERROR_CONFIGS: Record<string, ErrorConfig> = {
	'400': {
		code: '400',
		title: 'Yêu cầu không hợp lệ',
		message: 'Yêu cầu của bạn không đúng định dạng. Vui lòng kiểm tra lại thông tin.',
	},
	'401': {
		code: '401',
		title: 'Chưa xác thực',
		message: 'Bạn cần đăng nhập để truy cập trang này.',
	},
	'403': {
		code: '403',
		title: 'Không có quyền truy cập',
		message: 'Bạn không có quyền truy cập vào tài nguyên này.',
	},
	'404': {
		code: '404',
		title: 'Không tìm thấy',
		message: 'Trang hoặc tài nguyên bạn đang tìm không tồn tại.',
	},
	'500': {
		code: '500',
		title: 'Lỗi máy chủ',
		message: 'Đã xảy ra lỗi trên máy chủ. Vui lòng thử lại sau.',
	},
	'502': {
		code: '502',
		title: 'Lỗi cổng kết nối',
		message: 'Máy chủ nhận được phản hồi không hợp lệ. Vui lòng thử lại sau.',
	},
	'503': {
		code: '503',
		title: 'Dịch vụ không khả dụng',
		message: 'Hệ thống đang bảo trì. Vui lòng quay lại sau.',
	},
	'auth_error': {
		code: 'AUTH',
		title: 'Lỗi xác thực',
		message: 'Đã xảy ra lỗi trong quá trình đăng nhập. Vui lòng thử lại.',
	},
	'oauth_error': {
		code: 'OAUTH',
		title: 'Lỗi đăng nhập OAuth',
		message: 'Không thể đăng nhập bằng tài khoản Google. Vui lòng thử lại hoặc sử dụng phương thức khác.',
	},
};

const DEFAULT_ERROR: ErrorConfig = {
	code: 'ERROR',
	title: 'Đã xảy ra lỗi',
	message: 'Đã xảy ra lỗi không xác định. Vui lòng thử lại sau.',
};

export default function ErrorPage() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const errorCode = searchParams.get('code') || 'default';
	const customMessage = searchParams.get('message');
	
	const errorConfig = ERROR_CONFIGS[errorCode] || DEFAULT_ERROR;
	const displayMessage = customMessage || errorConfig.message;

	const handleGoHome = () => {
		navigate('/dashboard');
	};

	const handleGoLogin = () => {
		navigate('/login');
	};

	const showLoginButton = errorCode === '401' || errorCode === 'auth_error' || errorCode === 'oauth_error';

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				minHeight: '100vh',
				textAlign: 'center',
				gap: 2,
				px: 2,
			}}
		>
			<ErrorOutlineIcon 
				sx={{ 
					fontSize: '6rem', 
					color: 'error.main',
					mb: 2
				}} 
			/>
			
			<Typography 
				variant="h1" 
				component="h1" 
				sx={{ 
					fontSize: '4rem', 
					fontWeight: 'bold', 
					color: 'error.main' 
				}}
			>
				{errorConfig.code}
			</Typography>
			
			<Typography variant="h4" component="h2" gutterBottom>
				{errorConfig.title}
			</Typography>
			
			<Typography 
				variant="body1" 
				color="text.secondary" 
				sx={{ mb: 3, maxWidth: '600px' }}
			>
				{displayMessage}
			</Typography>
			
			<Box sx={{ display: 'flex', gap: 2 }}>
				{showLoginButton ? (
					<Button 
						variant="contained" 
						onClick={handleGoLogin}
					>
						Đăng nhập
					</Button>
				) : (
					<Button 
						variant="contained" 
						onClick={handleGoHome}
					>
						Về trang chủ
					</Button>
				)}
			</Box>
		</Box>
	);
}
